import { apiClient } from "@shared/lib/api-client";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { InferRequestType } from "hono";

/** A failed payments API call, with the API's error `code` when it sent one. */
export class PaymentsApiError extends Error {
	constructor(
		message: string,
		readonly status: number,
		readonly code?: string,
	) {
		super(message);
		this.name = "PaymentsApiError";
	}
}

export const createCustomerPortalLinkMutationKey = [
	"create-customer-portal-link",
] as const;
export const useCreateCustomerPortalLinkMutation = () => {
	return useMutation({
		mutationKey: createCustomerPortalLinkMutationKey,
		mutationFn: async (
			query: InferRequestType<
				(typeof apiClient.payments)["create-customer-portal-link"]["$post"]
			>["query"],
		) => {
			const response = await apiClient.payments[
				"create-customer-portal-link"
			].$post({
				query,
			});

			if (!response.ok) {
				// 409 PLAN_CHANGE_UNAVAILABLE answers JSON; other failures may
				// be plain text.
				const body = (await response.json().catch(() => null)) as {
					error?: string;
					code?: string;
				} | null;
				throw new PaymentsApiError(
					body?.error ?? "Failed to create customer portal link",
					response.status,
					body?.code,
				);
			}

			return response.json();
		},
	});
};

export const createCheckoutLinkMutationKey = ["create-checkout-link"] as const;
export const useCreateCheckoutLinkMutation = () => {
	return useMutation({
		mutationKey: createCheckoutLinkMutationKey,
		mutationFn: async (
			query: InferRequestType<
				(typeof apiClient.payments)["create-checkout-link"]["$post"]
			>["query"],
		) => {
			const response = await apiClient.payments[
				"create-checkout-link"
			].$post({
				query,
			});

			if (!response.ok) {
				throw new Error("Failed to create checkout link");
			}

			return response.json();
		},
	});
};

export const purchasesQueryKey = (
	propertyId?: string,
	organizationId?: string,
) =>
	propertyId
		? ["property", "purchases", propertyId]
		: (["organization", "purchases", organizationId] as const);

export const usePurchasesQuery = (
	propertyId?: string,
	organizationId?: string,
	options?: { enabled?: boolean },
) => {
	// Only enable the query if at least one of propertyId or organizationId is provided
	const shouldFetch = !!(propertyId || organizationId);

	return useQuery({
		queryKey: purchasesQueryKey(propertyId, organizationId),
		queryFn: async () => {
			const response = await apiClient.payments.purchases.$get({
				query: {
					propertyId,
					organizationId,
				},
			});

			if (!response.ok) {
				throw new Error("Failed to fetch purchases");
			}

			return response.json();
		},
		enabled: shouldFetch && options?.enabled !== false,
		...options,
	});
};

export const purchaseDocumentsQueryKey = (propertyId: string) =>
	["property", "purchase-documents", propertyId] as const;

export const usePurchaseDocumentsQuery = (
	propertyId: string,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: purchaseDocumentsQueryKey(propertyId),
		queryFn: async () => {
			const response = await apiClient.payments[
				"purchase-documents"
			].$get({
				query: {
					propertyId,
				},
			});

			if (!response.ok) {
				throw new Error("Failed to fetch purchase documents");
			}

			return response.json();
		},
		enabled: options?.enabled !== false,
		...options,
	});
};

// Organization-level purchase documents (invoices from Stripe)
export const organizationPurchaseDocumentsQueryKey = (organizationId: string) =>
	["organization", "purchase-documents", organizationId] as const;

export const useOrganizationPurchaseDocumentsQuery = (
	organizationId: string,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: organizationPurchaseDocumentsQueryKey(organizationId),
		queryFn: async () => {
			const response = await apiClient.payments[
				"purchase-documents"
			].$get({
				query: {
					organizationId,
				},
			});

			if (!response.ok) {
				throw new Error(
					"Failed to fetch organization purchase documents",
				);
			}

			return response.json();
		},
		enabled: options?.enabled !== false,
		...options,
	});
};

// Credit balance and history hooks
export const creditBalanceQueryKey = (organizationId: string) =>
	["organization", "credits", "balance", organizationId] as const;

export const useCreditBalanceQuery = (
	organizationId: string,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: creditBalanceQueryKey(organizationId),
		queryFn: async () => {
			const response = await apiClient.payments.credits.balance.$get({
				query: {
					organizationId,
				},
			});

			if (!response.ok) {
				throw new Error("Failed to fetch credit balance");
			}

			return response.json();
		},
		enabled: options?.enabled !== false,
		...options,
	});
};

export const creditHistoryQueryKey = (
	organizationId: string,
	options?: {
		limit?: number;
		offset?: number;
		reason?: string;
		startDate?: string;
		endDate?: string;
	},
) =>
	[
		"organization",
		"credits",
		"history",
		organizationId,
		options?.limit,
		options?.offset,
		options?.reason,
		options?.startDate,
		options?.endDate,
	] as const;

export const useCreditHistoryQuery = (
	organizationId: string,
	options?: {
		limit?: number;
		offset?: number;
		reason?: string;
		startDate?: string;
		endDate?: string;
		enabled?: boolean;
	},
) => {
	return useQuery({
		queryKey: creditHistoryQueryKey(organizationId, options),
		queryFn: async () => {
			const query: any = { organizationId };
			if (options?.limit) query.limit = options.limit.toString();
			if (options?.offset) query.offset = options.offset.toString();
			if (options?.reason) query.reason = options.reason;
			if (options?.startDate) query.startDate = options.startDate;
			if (options?.endDate) query.endDate = options.endDate;

			const response = await apiClient.payments.credits.history.$get({
				query,
			});

			if (!response.ok) {
				throw new Error("Failed to fetch credit history");
			}

			return response.json();
		},
		enabled: options?.enabled !== false,
		...options,
	});
};

// Per-property usage (accounting dashboard)
export interface UsageByPropertyResult {
	properties: Array<{
		id: string;
		name: string | null;
		domain: string | null;
		isProspect: boolean;
		isScoringOnly: boolean;
	}>;
	credits: Array<{
		propertyId: string | null;
		date: string;
		credits: number;
	}>;
	promptRuns: Array<{
		propertyId: string;
		date: string;
		runs: number;
	}>;
	overage: Array<{ propertyId: string | null; credits: number }>;
	range: { startDate: string; endDate: string };
}

export const usageByPropertyQueryKey = (
	organizationId: string,
	startDate?: string,
	endDate?: string,
) =>
	[
		"organization",
		"usage",
		"by-property",
		organizationId,
		startDate,
		endDate,
	] as const;

export const useUsageByPropertyQuery = (
	organizationId: string,
	options?: { startDate?: string; endDate?: string; enabled?: boolean },
) => {
	return useQuery({
		queryKey: usageByPropertyQueryKey(
			organizationId,
			options?.startDate,
			options?.endDate,
		),
		queryFn: async () => {
			const query: any = { organizationId };
			if (options?.startDate) query.startDate = options.startDate;
			if (options?.endDate) query.endDate = options.endDate;

			const response = await (apiClient.payments as any).usage[
				"by-property"
			].$get({
				query,
			});

			if (!response.ok) {
				throw new Error("Failed to fetch per-property usage");
			}

			return (await response.json()) as UsageByPropertyResult;
		},
		enabled: options?.enabled !== false && !!organizationId,
	});
};

// Placement scoring usage (accounting only — scoring is currently free)
export interface PlacementScoringUsageResult {
	used: number;
	/** null when no monthly ceiling is configured, which is the default. */
	cap: number | null;
	remaining: number | null;
	monthStart: string;
	monthEnd: string;
}

export const placementScoringUsageQueryKey = (organizationId: string) =>
	["organization", "usage", "placement-scoring", organizationId] as const;

export const usePlacementScoringUsageQuery = (
	organizationId: string,
	options?: { enabled?: boolean },
) => {
	return useQuery({
		queryKey: placementScoringUsageQueryKey(organizationId),
		queryFn: async () => {
			const response = await (apiClient.payments as any).usage[
				"placement-scoring"
			].$get({
				query: { organizationId },
			});

			if (!response.ok) {
				throw new Error("Failed to fetch placement scoring usage");
			}

			return (await response.json()) as PlacementScoringUsageResult;
		},
		enabled: options?.enabled !== false && !!organizationId,
	});
};

// Subscription cancellation mutations
export const cancelSubscriptionMutationKey = ["cancel-subscription"] as const;
export const useCancelSubscriptionMutation = () => {
	return useMutation({
		mutationKey: cancelSubscriptionMutationKey,
		mutationFn: async (organizationId: string) => {
			const response = await apiClient.payments[
				"cancel-subscription"
			].$post({
				json: { organizationId },
			});

			if (!response.ok) {
				const error = await response.json().catch(() => ({
					message: "Failed to cancel subscription",
				}));
				throw new Error(
					(error as any).message || "Failed to cancel subscription",
				);
			}

			return response.json();
		},
	});
};

export const reactivateSubscriptionMutationKey = [
	"reactivate-subscription",
] as const;
export const useReactivateSubscriptionMutation = () => {
	return useMutation({
		mutationKey: reactivateSubscriptionMutationKey,
		mutationFn: async (organizationId: string) => {
			const response = await apiClient.payments[
				"reactivate-subscription"
			].$post({
				json: { organizationId },
			});

			if (!response.ok) {
				const error = await response.json().catch(() => ({
					message: "Failed to reactivate subscription",
				}));
				throw new Error(
					(error as any).message ||
						"Failed to reactivate subscription",
				);
			}

			return response.json();
		},
	});
};
